import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ListChecks, LogOut, Settings } from "lucide-react";
import { Button } from "@ds/ui/button";
import { Row, RowGroup, RowIcon } from "@ds/patterns/row";
import { AthleteProfile } from "@/features/athlete";
import { useSignOut } from "@/shared/auth";

/**
 * YOU — the fourth tab: your maxes, your skills, the ladders you are on, and anything you
 * cannot do right now.
 *
 * ⚠️ THE SETTINGS ROW IS NOT DECORATION, it is the only remaining way in.
 *
 * The tab bar takes four items and the four are Today · Gym · History · You, so Settings
 * lost the tab it used to have. Everything on that screen — changing language, turning push
 * on, which build this is — would otherwise be reachable by typing a URL, which on a phone
 * means not at all. It sits here because "you" is what the screen is about, and it stays
 * here until something else leads to `/settings`.
 */
export const Route = createFileRoute("/_app/you")({
  /**
   * `?add=restriction` — ARRIVING HERE TO DECLARE SOMETHING (BUD-344).
   *
   * A session cancelled with "I hurt myself" sends the athlete straight to the restrictions
   * editor, because a restriction is the only thing in this app the engine actually plans
   * around: without one, tomorrow is lightened and still contains the movement that hurt.
   *
   * ⚠️ IT IS OPTIONAL IN THE OUTPUT TYPE and the default is omitted rather than written out,
   * for the same two reasons `/history` states on its own `?tab`: `/you` stays the canonical
   * address of this screen — the tab bar links to it — and every existing `navigate({ to:
   * "/you" })` stays legal without naming an intent it does not have.
   */
  validateSearch: (search: Record<string, unknown>): { add?: "restriction" } =>
    search.add === "restriction" ? { add: "restriction" } : {},
  component: YouScreen,
});

function YouScreen() {
  const { t } = useTranslation(["athlete", "common"]);
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { add } = Route.useSearch();

  return (
    <div className="flex flex-col gap-7">
      <div>
        <h1 className="text-title">{t("athlete:title")}</h1>
        <p className="text-support text-foreground-faint mt-0.5">{t("athlete:subtitle")}</p>
      </div>

      <AthleteProfile startAddingRestriction={add === "restriction"} />

      <RowGroup>
        {/*
          FIRST RUN, RE-ENTERED. BUD-293 asks that coming back here EDITS rather than
          duplicates, and it does: the gym step loads the default room and saves a diff of it,
          and every other step reads and writes the same profile this screen draws. So this is
          "go through the questions again", not "start a second setup".

          It lives beside Settings for the same reason Settings lives here — this screen is
          what "you" means, and a flow with no way back into it is a flow nobody re-enters.
        */}
        <Row
          leading={
            <RowIcon>
              <ListChecks />
            </RowIcon>
          }
          title={t("athlete:firstRun.title")}
          description={t("athlete:firstRun.support")}
          onSelect={() => void navigate({ to: "/first-run" })}
        />
        <Row
          leading={
            <RowIcon>
              <Settings />
            </RowIcon>
          }
          title={t("common:nav.settings")}
          onSelect={() => void navigate({ to: "/settings" })}
        />
      </RowGroup>

      {/*
        SIGNING OUT IS SECONDARY, AND THERE IS NO `navigate` AFTER IT.
        The route guards already own the redirect: when the session becomes null the status
        turns "anonymous", the router is invalidated, and `_app` redirects. Navigating here
        would run before React had re-rendered with the new session — the router's auth
        context would still say "authenticated", the sign-in route's own guard would bounce
        straight back, and the person who pressed the button would stay exactly where they
        were. `/settings` carries the same note over the same button and the same bug.

        And a single 401 never signs anyone out: that path is the query cache's, which
        re-checks the session once before acting. This button is the deliberate act.
      */}
      <Button variant="secondary" onClick={() => void signOut.mutateAsync()}>
        <LogOut />
        {t("common:actions.signOut")}
      </Button>
    </div>
  );
}
